import React from 'react'

/**
 * AbwaabPlus AvatarTile — a rounded tile showing an emoji (or image) on a
 * tinted fill. Used for course covers, teacher/student avatars, bundle covers.
 * `shape="circle"` for people, `shape="rounded"` for courses/bundles.
 */
export interface AbwAvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
	emoji?: React.ReactNode
	src?: string
	color?: string
	size?: number
	shape?: 'circle' | 'rounded'
	tint?: number
	online?: boolean
}

export function Avatar({ emoji, src, color = 'var(--blue)', size = 48, shape = 'circle', tint = 0.12, online = false, style, ...rest }: AbwAvatarProps) {
	const radius = shape === 'circle' ? '50%' : size >= 72 ? 'var(--r-2xl)' : 'var(--r-xl)'
	// color may be a CSS var() or a hex; build a tinted background.
	const bg = color.startsWith('var')
		? `color-mix(in srgb, ${color} ${tint * 100}%, transparent)`
		: `${color}${Math.round(tint * 255)
				.toString(16)
				.padStart(2, '0')}`
	return (
		<span style={{ position: 'relative', flex: 'none', display: 'inline-block' }}>
			<span
				style={{
					width: size,
					height: size,
					borderRadius: radius,
					// longhand properties only — mixing the `background` shorthand with
					// backgroundClip makes React warn on rerender
					backgroundColor: src ? undefined : bg,
					backgroundImage: src ? `url(${src})` : undefined,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					// keep the cover image from bleeding under any ring/border the caller adds
					backgroundClip: 'padding-box',
					overflow: 'hidden',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: Math.round(size * 0.45),
					...style,
				}}
				{...rest}
			>
				{!src && emoji}
			</span>
			{online && (
				<span
					style={{
						position: 'absolute',
						bottom: 0,
						insetInlineEnd: 0,
						width: Math.max(8, size * 0.22),
						height: Math.max(8, size * 0.22),
						borderRadius: '50%',
						background: 'var(--success)',
						border: '2px solid var(--surface-card)',
					}}
				/>
			)}
		</span>
	)
}
